import { nanoid } from 'nanoid';

import { AUDIENCE_METADATA } from './metadata';

export function generateEmptyCondition(criteria) {
  return {
    criteria,
    rows: [generateEmptyConditionRow(criteria)],
  };
}

export function generateEmptyConditionRow(criteria) {
  const { comparisons, defaultValue } = AUDIENCE_METADATA[criteria];

  return {
    id: nanoid(),
    values: cloneDefaultValue(defaultValue ?? []),
    comparison: comparisons?.[0].value ?? null,
  };
}

function cloneDefaultValue(defaultValue) {
  if (Array.isArray(defaultValue)) {
    return [...defaultValue];
  }

  if (defaultValue && typeof defaultValue === 'object') {
    return { ...defaultValue };
  }

  return defaultValue;
}

export function addCriteriaToDraft(draft, newCriteria, currentIndex = null) {
  const conditions = draft?.conditions ?? [];

  if (currentIndex != null && conditions[currentIndex]?.criteria === newCriteria) {
    return draft;
  }

  const { multiAnd, multiOr } = AUDIENCE_METADATA[newCriteria];
  const existingIndex = conditions.findIndex((condition) => condition.criteria === newCriteria);
  let nextConditions = conditions;

  if (existingIndex === -1) {
    nextConditions = [...nextConditions, generateEmptyCondition(newCriteria)];
  } else if (multiAnd) {
    nextConditions = nextConditions.map((condition, index) => {
      if (index !== existingIndex) {
        return condition;
      }

      const clearedCondition = clearConditionTooltipMarkers(condition);

      return {
        ...clearedCondition,
        rows: [...clearedCondition.rows, markRowValueTooltip(generateEmptyConditionRow(newCriteria))],
      };
    });
  } else if (multiOr) {
    nextConditions = nextConditions.map((condition, index) => {
      if (index !== existingIndex) {
        return condition;
      }

      const clearedCondition = clearConditionTooltipMarkers(condition);
      const lastIndex = condition.rows.length - 1;

      return {
        ...clearedCondition,
        rows: clearedCondition.rows.map((row, rowIndex) =>
          rowIndex === lastIndex
            ? {
                ...row,
                values: [...row.values, ''],
                valueTooltips: [...(row.valueTooltips ?? []), row.values.length],
              }
            : row,
        ),
      };
    });
  } else {
    nextConditions = markConditionAtIndexTooltip(nextConditions, existingIndex, 'criteria');
  }

  if (currentIndex != null) {
    nextConditions = nextConditions.filter((_, index) => index !== currentIndex);
  }

  if (nextConditions === conditions) {
    return draft;
  }

  return {
    ...draft,
    conditions: nextConditions,
  };
}

function markConditionTooltip(condition, tooltip) {
  return {
    ...clearConditionTooltipMarkers(condition),
    tooltip,
  };
}

function markConditionAtIndexTooltip(conditions, conditionIndex, tooltip) {
  return conditions.map((condition, index) =>
    index === conditionIndex ? markConditionTooltip(condition, tooltip) : condition,
  );
}

function markRowValueTooltip(row) {
  if (Array.isArray(row.values)) {
    return {
      ...row,
      valueTooltips: [...(row.valueTooltips ?? []), row.values.length - 1],
    };
  }

  return {
    ...row,
    tooltip: 'value',
  };
}

function clearConditionTooltipMarkers(condition) {
  const rows = condition.rows.map((row) => {
    if (!row.tooltip && (row.valueTooltips ?? []).length === 0) {
      return row;
    }

    return {
      ...row,
      tooltip: false,
      valueTooltips: [],
    };
  });

  if (!condition.tooltip && rows === condition.rows) {
    return condition;
  }

  return {
    ...condition,
    ...(condition.tooltip ? { tooltip: false } : {}),
    rows,
  };
}

export function addCondition(currentIndex, newCriteria, values, setFieldValue) {
  const nextDraft = addCriteriaToDraft(values, newCriteria, currentIndex);

  if (nextDraft?.conditions !== values.conditions) {
    setFieldValue('conditions', nextDraft.conditions);
  }
}

export function createInitialDraft(criteria) {
  return {
    conditions: [generateEmptyCondition(criteria)],
  };
}

function serializeValue(criteria, values = [], comparisonJson) {
  return AUDIENCE_METADATA[criteria]?.serializeValue?.(values, comparisonJson) ?? values;
}

function deserializeValue(criteria, value = [], comparisonJson) {
  return AUDIENCE_METADATA[criteria]?.deserializeValue?.(value, comparisonJson) ?? value;
}

function getComparisonDefinitions(criteria) {
  return AUDIENCE_METADATA[criteria].comparisons ?? [];
}

function getDraftComparison(criteria, comparisonJson, positive) {
  return getComparisonDefinitions(criteria).find((item) => item.json === comparisonJson && item.positive === positive)
    .value;
}

function getDefinitionComparison(criteria, comparisonValue) {
  return getComparisonDefinitions(criteria).find((item) => item.value === comparisonValue);
}

function appendDraftRow(conditionsByCriteria, condition, comparison, positive) {
  const existingCondition = conditionsByCriteria.get(condition.criteria);
  const row = {
    id: nanoid(),
    comparison: getDraftComparison(condition.criteria, comparison, positive),
    values: deserializeValue(condition.criteria, condition.value, comparison),
  };

  if (existingCondition) {
    existingCondition.rows.push(row);
    return;
  }

  conditionsByCriteria.set(condition.criteria, {
    criteria: condition.criteria,
    rows: [row],
  });
}

function compareConditionsByCriteriaLabel(leftCondition, rightCondition) {
  const leftLabel = AUDIENCE_METADATA[leftCondition.criteria]?.label ?? leftCondition.criteria;
  const rightLabel = AUDIENCE_METADATA[rightCondition.criteria]?.label ?? rightCondition.criteria;

  return leftLabel.localeCompare(rightLabel) || leftCondition.criteria.localeCompare(rightCondition.criteria);
}

export function definitionToDraft(definition) {
  if (!definition) {
    return { conditions: [] };
  }

  const conditionsByCriteria = new Map();

  for (const condition of definition.conditions ?? []) {
    appendDraftRow(conditionsByCriteria, condition, condition.comparison, true);
  }

  for (const condition of definition.except?.conditions ?? []) {
    appendDraftRow(conditionsByCriteria, condition, condition.comparison, false);
  }

  return {
    conditions: [...conditionsByCriteria.values()].sort(compareConditionsByCriteriaLabel),
  };
}

export function draftToDefinition(draft) {
  const positiveConditions = [];
  const negativeConditions = [];

  for (const condition of draft?.conditions ?? []) {
    for (const row of condition.rows ?? []) {
      const comparison = getDefinitionComparison(condition.criteria, row.comparison);
      const target = comparison.positive ? positiveConditions : negativeConditions;

      target.push({
        criteria: condition.criteria,
        comparison: comparison.json,
        value: serializeValue(condition.criteria, row.values, comparison.json),
      });
    }
  }

  if (positiveConditions.length === 0 && negativeConditions.length === 0) {
    return null;
  }

  return {
    version: 1,
    operator: 'and',
    conditions: positiveConditions,
    ...(negativeConditions.length > 0
      ? {
          except: {
            operator: 'or',
            conditions: negativeConditions,
          },
        }
      : {}),
  };
}

export function formatAudienceSummary(
  definition,
  { optionLabelsByCriteria = {}, missingAsyncLabel = '<deleted>' } = {},
) {
  return definitionToDraft(definition)
    .conditions.map((condition) => ({
      criteria: AUDIENCE_METADATA[condition.criteria]?.label ?? condition.criteria,
      rows: condition.rows.map((row) =>
        formatAudienceSummaryRow(condition.criteria, row, {
          optionLabelsByCriteria,
          missingAsyncLabel,
        }),
      ),
    }))
    .map((summary) => ({
      ...summary,
      text: summary.rows.map((row) => row.text).join(' AND '),
    }));
}

function formatAudienceSummaryRow(criteria, row, options) {
  const metadata = AUDIENCE_METADATA[criteria];
  const comparison = formatAudienceComparison(row.comparison, metadata);
  const value = formatAudienceSummaryValues(criteria, row.values, row.comparison, options);

  return {
    comparison,
    comparisonPlacement: shouldPlaceComparisonWithValue(metadata, comparison) ? 'value' : 'label',
    value,
    text: comparison ? `${comparison} ${value}` : value,
  };
}

function shouldPlaceComparisonWithValue(metadata, comparison) {
  return Boolean(comparison && ['number', 'duration', 'money'].includes(metadata?.type));
}

function formatAudienceComparison(comparison, metadata) {
  if (metadata?.hiddenComparison || !comparison || comparison === 'is' || comparison === 'did') {
    return '';
  }

  const normalizedComparison = comparison.replace(/\s+/g, ' ');

  if (['number', 'duration', 'money'].includes(metadata?.type)) {
    return NUMERIC_SUMMARY_COMPARISONS[normalizedComparison] ?? normalizedComparison;
  }

  return normalizedComparison;
}

const NUMERIC_SUMMARY_COMPARISONS = {
  'greater than': '>',
  'less than': '<',
  equals: '=',
  'at least': '≥',
  'at most': '≤',
};

function formatAudienceSummaryValues(criteria, values, comparison, options) {
  const metadata = AUDIENCE_METADATA[criteria];

  if (metadata?.type === 'number') {
    return formatRangeValue(values);
  }

  if (metadata?.type === 'duration') {
    return formatDurationValue(values, comparison);
  }

  if (metadata?.type === 'money') {
    return formatMoneyValue(values, comparison);
  }

  if (!Array.isArray(values)) {
    return formatAudienceSummaryValue(criteria, values, options);
  }

  return values.map((value) => formatAudienceSummaryValue(criteria, value, options)).join(' OR ');
}

function formatAudienceSummaryValue(criteria, value, { optionLabelsByCriteria, missingAsyncLabel }) {
  const metadata = AUDIENCE_METADATA[criteria];
  const asyncLabel = getAsyncOptionLabel(optionLabelsByCriteria?.[criteria], value);

  if (asyncLabel != null) {
    return asyncLabel;
  }

  if (metadata?.type === 'asyncSelect' && optionLabelsByCriteria?.[criteria] && value != null && value !== '') {
    return missingAsyncLabel;
  }

  const option = metadata?.options?.find((option) => String(option.value) === String(value));

  if (option) {
    return option.label;
  }

  if (value && typeof value === 'object' && 'url' in value) {
    return value.url;
  }

  return String(value ?? '');
}

function getAsyncOptionLabel(optionLabels, value) {
  if (!optionLabels || value == null || value === '') {
    return null;
  }

  if (optionLabels instanceof Map) {
    return optionLabels.get(String(value)) ?? null;
  }

  return optionLabels[String(value)] ?? null;
}

function formatRangeValue(values = {}) {
  if (values?.max != null && values.max !== '') {
    return `${values.amount} and ${values.max}`;
  }

  return String(values?.amount ?? '');
}

function formatDurationValue(values = {}, comparison) {
  const unit = values?.unit ?? '';

  if (comparison === 'between') {
    return `${values?.amount ?? ''} and ${values?.max ?? ''} ${unit}`.trim();
  }

  return `${values?.amount ?? ''} ${unit}`.trim();
}

function formatMoneyValue(values = {}, comparison) {
  const currency = values?.currency ?? '';

  if (comparison === 'between') {
    return `${values?.amount ?? ''} and ${values?.max ?? ''} ${currency}`.trim();
  }

  return `${values?.amount ?? ''} ${currency}`.trim();
}
